import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AppWindow, Fingerprint, Tag, ShieldAlert, Video, Globe2, Sparkles, Target, Users } from "lucide-react";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { LandingFooter } from "@/components/landing/LandingFooter";

const ADVANTAGES = [
  { icon: AppWindow, title: "Полноценное ПО", desc: "Не сайт в браузере, а настоящее приложение для компьютера и телефона." },
  { icon: Fingerprint, title: "Один сертификат", desc: "Подписывайте с ноутбука, из командировки и с телефона — сертификат работает везде." },
  { icon: Tag, title: "Честная цена", desc: "1,99 ₽ за документ — ровно столько стоит обработка, без скрытых условий." },
  { icon: ShieldAlert, title: "Проверка контрагентов", desc: "Автоматическая сверка партнёра по базам ещё до подписания документа." },
  { icon: Video, title: "Связь с партнёром", desc: "Звонок прямо из переписки по документу — без переключения в другие сервисы." },
  { icon: Globe2, title: "Роуминг со всеми ЭДО", desc: "Обмен работает с другими операторами — неважно, чем пользуется партнёр." },
];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export default function AboutCompany() {
  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="about-company-page">
      <LandingHeader />

      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-[#2563EB] text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5" /> О компании
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="about-hero-title">
              Мы решили, что обмен документами<br className="hidden sm:block" /> должен быть простым
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
              КриптоДок создан компанией ООО «Консорциум Инновационных Алгоритмических Педагогических Систем»
              (KIAPS LAB) — командой специалистов из ведущих технологических компаний, объединившихся
              в 2026 году вокруг одной идеи.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 border border-slate-100">
              <img
                src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Команда КриптоДок за работой"
                className="w-full h-full object-cover"
                data-testid="about-team-photo"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Как всё начиналось</h2>
              <p className="text-slate-600 leading-relaxed">
                Наша команда — выходцы из ведущих ИТ- и алгоритмических компаний страны. Работая над
                сложными цифровыми системами, мы снова и снова сталкивались с одной и той же проблемой:
                электронный документооборот в России давно цифровой, но всё ещё неудобный — десятки
                операторов, которые плохо совместимы друг с другом, запутанные тарифы и сертификаты,
                привязанные к одному устройству.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Мы решили применить наш опыт в алгоритмических и образовательных технологиях не к очередной
                нишевой задаче, а к тому, с чем сталкивается каждая компания в стране — обмену юридически
                значимыми документами. Так появился КриптоДок: мы собрали лучшее, что есть в разных сервисах
                ЭДО, в одном месте, и убрали всё, что обычно раздражает.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Что мы изменили</h2>
              <p className="mt-2 text-slate-500 max-w-xl mx-auto">
                Мы собрали сильные стороны разных решений в одном продукте — и убрали лишние барьеры.
              </p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ADVANTAGES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <div
                  className="h-full p-6 rounded-2xl border border-slate-100 bg-[#F8FAFC] hover:shadow-md hover:border-blue-100 transition-all"
                  data-testid={`about-advantage-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] flex items-center justify-center mb-4">
                    <a.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1.5">{a.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full p-8 rounded-2xl bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] text-white shadow-lg shadow-blue-200">
              <Target className="w-8 h-8 mb-4" />
              <h3 className="text-xl font-bold mb-2">Наша миссия</h3>
              <p className="text-blue-50 leading-relaxed">
                Сделать юридически значимый документооборот доступным и понятным для любого бизнеса —
                от небольшого ИП до промышленного холдинга — без переплат и лишних сложностей.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full p-8 rounded-2xl border border-slate-100 bg-white">
              <Users className="w-8 h-8 mb-4 text-[#4E9BEE]" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Консорциум</h3>
              <p className="text-slate-600 leading-relaxed">
                KIAPS LAB объединяет инженеров, аналитиков и специалистов по информационной безопасности
                с опытом работы в крупнейших технологических компаниях страны — все они работают над
                КриптоДок как единая команда.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center rounded-3xl border border-slate-100 bg-white p-10 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900">Попробуйте КриптоДок прямо сейчас</h2>
            <p className="mt-2 text-slate-500">Полный доступ к демо-кабинету — без регистрации.</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/register"
                data-testid="about-cta-register"
                className="px-6 py-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#4E9BEE] to-[#2563EB] hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-md shadow-blue-200"
              >
                Начать бесплатно
              </Link>
              <Link
                to="/?embed=1#how-it-works"
                data-testid="about-cta-demo"
                className="px-6 py-3 rounded-lg text-sm font-semibold text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors"
              >
                Попробовать демо
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <LandingFooter />
    </div>
  );
}
